import { HotClubHttpClientProvider } from '@hot-club-http/client/devtools'

import { OptimizelyProvider } from '@optimizely/react-sdk'
import { ThemeWrapper } from '@space-core/context/theme'

import type { ThemeMode } from '@space-core/styles/themes/enum'

import { EmojiUtils } from '@space-core/utils/emoji'
import type { AuthContextProps } from '@space-shell/common/contexts/auth'
import { AuthServerProvider } from '@space-shell/common/contexts/auth'
import type { Membership } from '@space-shell/common/services/membership'
import { optimizelyClient, optimizelyLogConfig } from '@space-shell/common/utils/optimizely'
import { queryClient, isActiveQueryDevtools } from '@space-shell/config/request'

import { GlobalStyle } from '@space-shell/config/styles/global'
import type { AuthType } from '@space-shell/core/store'

import { HelmetProvider } from 'react-helmet-async'

import {
  useInitializeStore,
  usePixel,
  useVisitorId,
  useToastEventListener,
  useLanguageSetup
} from './hooks'
import Router from './router/router'

import '@space-shell/config/request/providers/hotClubHttp/hotClubHttpStart'
import '@space-shell/config/i18n'
import '@space-shell/lib/runtime/init'

EmojiUtils?.init?.()
EmojiUtils?.initData?.()
optimizelyLogConfig()

type MainProps = {
  membershipInfo: Membership
  locale: string
  user: AuthType
} & AuthContextProps

const Main = ({ authStatus, user, membershipInfo, locale }: MainProps) => {
  useToastEventListener()
  useInitializeStore({ user, membershipInfo })
  usePixel({ user, membershipInfo })
  useLanguageSetup({ language: locale })

  const visitorId = useVisitorId()
  const creatorTheme = membershipInfo?.settings?.theme?.toLocaleLowerCase()

  const configOptimizely = {
    id: user?.ucode || visitorId,
    attributes: {
      ownerUcode: membershipInfo?.ownerUcode || ''
    }
  }

  return (
    <ThemeWrapper
      allowChangeTheme={membershipInfo?.settings?.allowChangeTheme}
      creatorTheme={creatorTheme as ThemeMode}>
      <GlobalStyle />
      <HelmetProvider>
        <HotClubHttpClientProvider
          queryClient={queryClient}
          activeQueryDevtools={isActiveQueryDevtools}>
          <AuthServerProvider authStatus={authStatus}>
            <OptimizelyProvider optimizely={optimizelyClient} user={configOptimizely} timeout={500}>
              <div id='app-space-microfront' className='_vw-full _vh-full'>
                <Router />
              </div>
            </OptimizelyProvider>
          </AuthServerProvider>
        </HotClubHttpClientProvider>
      </HelmetProvider>
    </ThemeWrapper>
  )
}

export default Main
